import { useState } from 'react';
import type { ProgramPublicProfile } from '@/mocks/programProfile';
import { getSocialStats } from '@/mocks/rfwSocial';
import ShareModal from '@/components/feature/ShareModal';
import LikeButton from '@/components/feature/LikeButton';
import ViralStats from '@/components/feature/ViralStats';

interface Props {
  profile: ProgramPublicProfile;
}

export default function ProgramCoverHeader({ profile }: Props) {
  const [isFollowing, setIsFollowing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [notificationsOn, setNotificationsOn] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);

  const socialStats = getSocialStats('program', profile.id);

  const handleFollow = () => {
    setIsFollowing(!isFollowing);
  };

  const handleNotifications = () => {
    setNotificationsOn(!notificationsOn);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setShowShareToast(true);
    setTimeout(() => setShowShareToast(false), 2000);
  };  // keep for internal use

  const followersDisplay = isFollowing ? profile.followers + 1 : profile.followers;

  return (
    <>
      {/* Cover Image */}
      <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
        <img
          src={profile.coverUrl}
          alt={profile.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/5 to-black/55"></div>

        {/* Content overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8">
          <div className="max-w-5xl mx-auto">
            {/* Category badge */}
            <span className="inline-block px-3 py-1 rounded-full text-[11px] font-bold bg-white/20 text-white backdrop-blur-sm mb-3">
              {profile.category}
            </span>

            {/* Name */}
            <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">{profile.name}</h1>

            {/* Meta row */}
            <div className="flex items-center gap-3 md:gap-5 flex-wrap text-white/90 text-xs md:text-sm">
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full overflow-hidden ring-1 ring-white/30 shrink-0">
                  <img src={profile.hostPhotoUrl} alt={profile.hostName} className="w-full h-full object-cover" />
                </div>
                <span>{profile.hostName}</span>
              </div>
              <span className="text-white/40">·</span>
              <span>{profile.radioName} {profile.radioFrequency}</span>
              <span className="text-white/40">·</span>
              <span className="flex items-center gap-1">
                <i className={`w-2 h-2 rounded-full ${profile.isActive ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`}></i>
                {profile.isActive ? 'Al aire' : 'Pausado'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats + Actions Bar */}
      <section className="bg-background-50 border-b border-background-200/70 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* Stats */}
            <div className="flex items-center gap-4 md:gap-6">
              <div className="text-center">
                <p className="text-sm font-bold text-foreground-950">{formatNumber(followersDisplay)}</p>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Seguidores</p>
              </div>
              <div className="w-px h-8 bg-background-200"></div>
              <div className="text-center">
                <p className="text-sm font-bold text-foreground-950">{profile.episodes.length}</p>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Episodios</p>
              </div>
              <div className="w-px h-8 bg-background-200"></div>
              <div className="text-center">
                <p className="text-sm font-bold text-foreground-950">{profile.tags.length}</p>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Temas</p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleFollow}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  isFollowing
                    ? 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
              >
                <i className={`${isFollowing ? 'ri-user-follow-line' : 'ri-user-add-line'} text-base`}></i>
                {isFollowing ? 'Siguiendo' : 'Seguir'}
              </button>

              {/* Me Gusta */}
              <LikeButton
                entityId={profile.id}
                entityType="program"
                initialLikes={socialStats?.likes || 0}
                size="md"
              />

              {/* Share */}
              <button
                onClick={() => setShowShareModal(true)}
                className="w-9 h-9 rounded-full bg-secondary-100 hover:bg-secondary-200 flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-share-forward-line text-secondary-700 text-sm"></i>
              </button>

              {/* Notifications */}
              <button
                onClick={handleNotifications}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                  notificationsOn
                    ? 'bg-accent-100 text-accent-600 hover:bg-accent-200'
                    : 'bg-secondary-100 text-secondary-500 hover:bg-secondary-200'
                }`}
              >
                <i className={`${notificationsOn ? 'ri-notification-3-fill' : 'ri-notification-3-line'} text-sm`}></i>
              </button>

              {/* Viral Stats compact */}
              {socialStats && (
                <div className="hidden md:block ml-3 pl-3 border-l border-background-200">
                  <ViralStats
                    shares={socialStats.shares}
                    reach={socialStats.reach}
                    interactions={socialStats.interactions}
                    trendPercent={socialStats.trendPercent}
                    trendDirection={socialStats.trendDirection}
                    compact
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Share toast */}
      {showShareToast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 bg-foreground-900 text-white px-5 py-3 rounded-full text-sm font-medium shadow-lg animate-pulse">
          <i className="ri-check-line mr-2"></i>
          Enlace copiado al portapapeles
        </div>
      )}

      {/* Share Modal */}
      <ShareModal
        entityName={profile.name}
        entitySubtitle={profile.category}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </>
  );
}

function formatNumber(n: number): string {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(0) + 'K';
  return n.toString();
}